import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowLeftRight, ExternalLink } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  BD_CITIES,
  BD_CITY_MAP,
  cityDistance,
  haversineKm,
  bearingDeg,
  compassDirection,
  distanceFromSpeedTime,
  timeFromDistanceSpeed,
  speedFromDistanceTime,
  hoursToHm,
  type SpeedUnit,
} from "@/lib/calculators/distance";
import {
  convertLength,
  convertToAll,
  LENGTH_UNITS,
  type LengthUnitKey,
} from "@/lib/calculators/feetToMeter";

type B = { en: string; bn: string };
type Lang = "en" | "bn";
type TabKey = "cities" | "coords" | "units" | "st";

const T = {
  h1: { en: "Distance Calculator", bn: "দূরত্ব ক্যালকুলেটর" },
  desc: {
    en: "Distance between Bangladesh cities, lat/lon (Haversine), unit conversion, and speed × time travel calculations — all in one tool.",
    bn: "বাংলাদেশের শহরের মধ্যে দূরত্ব, অক্ষাংশ-দ্রাঘিমাংশ (Haversine), একক রূপান্তর এবং গতি × সময় ভ্রমণের হিসাব — সব এক জায়গায়।",
  },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  tCities: { en: "BD Cities", bn: "শহর" },
  tCoords: { en: "Lat / Lon", bn: "অক্ষাংশ/দ্রাঘিমাংশ" },
  tUnits:  { en: "Unit Convert", bn: "একক রূপান্তর" },
  tST:     { en: "Speed × Time", bn: "গতি × সময়" },
} as const;

const FAQ: { q: B; a: B }[] = [
  { q: { en: "What is the distance between Dhaka and Chittagong?", bn: "ঢাকা থেকে চট্টগ্রামের দূরত্ব কত?" },
    a: { en: "By road: about 264 km (5–6 hours). By air: 244 km (~50 min flight). Direct great-circle distance is roughly 209 km.",
         bn: "সড়কপথে প্রায় ২৬৪ কিমি (৫–৬ ঘণ্টা)। আকাশপথে ২৪৪ কিমি (~৫০ মিনিট ফ্লাইট)। সরাসরি (great-circle) দূরত্ব ≈ ২০৯ কিমি।" } },
  { q: { en: "How far is Cox's Bazar from Dhaka?", bn: "ঢাকা থেকে কক্সবাজার কত দূর?" },
    a: { en: "Dhaka → Cox's Bazar is 414 km by road (9–10 hours via Dhaka–Chittagong highway) or 333 km by air (~65 min).",
         bn: "ঢাকা → কক্সবাজার সড়কপথে ৪১৪ কিমি (৯–১০ ঘণ্টা, ঢাকা-চট্টগ্রাম মহাসড়ক হয়ে); আকাশপথে ৩৩৩ কিমি (~৬৫ মিনিট)।" } },
  { q: { en: "ঢাকা থেকে চট্টগ্রাম কত কিলোমিটার?", bn: "ঢাকা থেকে চট্টগ্রাম কত কিলোমিটার?" },
    a: { en: "ঢাকা থেকে চট্টগ্রাম সড়কপথে ২৬৪ কিলোমিটার।", bn: "ঢাকা থেকে চট্টগ্রাম সড়কপথে ২৬৪ কিলোমিটার।" } },
  { q: { en: "ঢাকা থেকে কক্সবাজার কত দূর?", bn: "ঢাকা থেকে কক্সবাজার কত দূর?" },
    a: { en: "ঢাকা → কক্সবাজার = ৪১৪ কিমি সড়কপথে।", bn: "ঢাকা → কক্সবাজার = ৪১৪ কিমি সড়কপথে।" } },
  { q: { en: "What is the Haversine formula?", bn: "Haversine সূত্র কী?" },
    a: { en: "Haversine computes the shortest distance between two points on a sphere from their latitude and longitude. d = 2R · asin(√(sin²(Δφ/2) + cos φ₁·cos φ₂·sin²(Δλ/2))), with R = 6,371 km for Earth.",
         bn: "Haversine দুটি বিন্দুর অক্ষাংশ-দ্রাঘিমাংশ থেকে গোলকের উপর সংক্ষিপ্ততম দূরত্ব নির্ণয় করে। R = ৬৩৭১ কিমি।" } },
  { q: { en: "Why is air distance shorter than road distance?", bn: "আকাশপথ সড়কপথের চেয়ে কম কেন?" },
    a: { en: "Roads follow rivers, terrain and existing infrastructure, so they're longer. Aircraft fly close to the great-circle path, which is the shortest line on a sphere.",
         bn: "সড়কগুলো নদী, ভূপ্রকৃতি ও বিদ্যমান অবকাঠামো ঘুরে যায়, তাই দীর্ঘ। বিমান প্রায় great-circle পথে চলে — গোলকের উপর সংক্ষিপ্ততম লাইন।" } },
  { q: { en: "How do I convert km to miles?", bn: "কিমি থেকে মাইলে কীভাবে?" },
    a: { en: "1 km = 0.621371 miles. So multiply km by 0.621371. 100 km = 62.14 miles.",
         bn: "১ কিমি = ০.৬২১৩৭১ মাইল। ১০০ কিমি = ৬২.১৪ মাইল।" } },
  { q: { en: "How long to drive 100 km at 80 km/h?", bn: "৮০ কিমি/ঘণ্টায় ১০০ কিমি যেতে কত সময়?" },
    a: { en: "Time = distance ÷ speed = 100 ÷ 80 = 1.25 hours = 1 hour 15 minutes.",
         bn: "সময় = দূরত্ব ÷ গতি = ১০০ ÷ ৮০ = ১.২৫ ঘণ্টা = ১ ঘণ্টা ১৫ মিনিট।" } },
  { q: { en: "Distance between Dhaka and Sylhet?", bn: "ঢাকা থেকে সিলেট কত কিমি?" },
    a: { en: "247 km by road (5–6 hr); 199 km by air (~45 min); 319 km by train (~7 hr).",
         bn: "সড়কপথে ২৪৭ কিমি (৫–৬ ঘণ্টা); আকাশপথে ১৯৯ কিমি (~৪৫ মিনিট); ট্রেনে ৩১৯ কিমি (~৭ ঘণ্টা)।" } },
  { q: { en: "Distance between Dhaka and Khulna?", bn: "ঢাকা থেকে খুলনা কত কিমি?" },
    a: { en: "270 km by road via Padma Bridge (~6 hr); 154 km by air (~50 min).",
         bn: "পদ্মা সেতু হয়ে সড়কপথে ২৭০ কিমি (~৬ ঘণ্টা); আকাশপথে ১৫৪ কিমি (~৫০ মিনিট)।" } },
];

export const Route = createFileRoute("/distance-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Distance Calculator — Bangladesh Cities & Lat/Lon",
      description:
        "Free distance calculator. Distance between Bangladesh cities (Dhaka to Chittagong, Sylhet, Cox's Bazar). Lat/lon Haversine distance. দূরত্ব ক্যালকুলেটর।",
      path: "/distance-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Distance", path: "/distance-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: DistanceCalculatorPage,
});

function stripBn(s: string): string {
  return s.replace(/[০-৯]/g, (d) => "০১২৩৪৫৬৭৮৯".indexOf(d).toString());
}

function DistanceCalculatorPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);
  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number, d = 2) => {
    if (!Number.isFinite(n)) return "—";
    const s = (Math.round(n * 10 ** d) / 10 ** d).toLocaleString("en-US", { maximumFractionDigits: d });
    return useBn ? toBengaliNumerals(s) : s;
  };

  const [tab, setTab] = useState<TabKey>("cities");

  return (
    <CalculatorLayout
      slug="distance-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="MapPin"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="mb-6 flex flex-wrap items-center justify-end gap-3 print:hidden">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 rounded-xl border border-border bg-card p-1">
        {([
          ["cities", tr("tCities")],
          ["coords", tr("tCoords")],
          ["units",  tr("tUnits")],
          ["st",     tr("tST")],
        ] as [TabKey, string][]).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              "flex-1 rounded-lg px-3 py-2 text-xs font-medium transition-colors",
              tab === k ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
              lang === "bn" && "font-bn",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "cities" && <CitiesTab lang={lang} useBn={useBn} num={num} fmt={fmt} />}
        {tab === "coords" && <CoordsTab lang={lang} useBn={useBn} num={num} fmt={fmt} />}
        {tab === "units"  && <UnitsTab  lang={lang} useBn={useBn}          fmt={fmt} />}
        {tab === "st"     && <SpeedTimeTab lang={lang} useBn={useBn} num={num} fmt={fmt} />}
      </div>

      {/* Reference table — popular routes */}
      <section className="mt-12 space-y-3">
        <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
          {lang === "en" ? "Popular Bangladesh routes" : "বাংলাদেশের জনপ্রিয় রুট"}
        </h2>
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="p-2 text-left">{lang === "en" ? "Route" : "রুট"}</th>
                <th className="p-2 text-right">{lang === "en" ? "Road km" : "সড়ক কিমি"}</th>
                <th className="p-2 text-right">{lang === "en" ? "Road time" : "সড়ক সময়"}</th>
                <th className="p-2 text-right">{lang === "en" ? "Air km" : "আকাশ কিমি"}</th>
                <th className="p-2 text-right">{lang === "en" ? "Train km" : "ট্রেন কিমি"}</th>
              </tr>
            </thead>
            <tbody>
              {POPULAR_ROUTES.map((r) => {
                const cd = cityDistance(r.from, r.to);
                if (!cd?.preset) return null;
                const p = cd.preset;
                const fromName = lang === "en" ? cd.from.en : cd.from.bn;
                const toName = lang === "en" ? cd.to.en : cd.to.bn;
                const t = p.road ? hoursToHm(p.road.minutes / 60) : null;
                return (
                  <tr key={`${r.from}-${r.to}`} className="border-t border-border">
                    <td className="p-2">{fromName} → {toName}</td>
                    <td className="p-2 text-right tabular-nums">{p.road ? num(p.road.km) : "—"}</td>
                    <td className="p-2 text-right tabular-nums">{t ? `${num(t.h)}h ${num(t.m)}m` : "—"}</td>
                    <td className="p-2 text-right tabular-nums">{p.air ? num(p.air.km) : "—"}</td>
                    <td className="p-2 text-right tabular-nums">{p.train ? num(p.train.km) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Haversine formula" : "Haversine সূত্র"}
          </h2>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "The Haversine formula computes the great-circle distance between two points on a sphere from their latitude and longitude. It assumes Earth is a perfect sphere with R = 6,371 km — accurate to within ~0.5% for most distances."
              : "Haversine সূত্র দুটি বিন্দুর অক্ষাংশ-দ্রাঘিমাংশ থেকে গোলকের উপর great-circle দূরত্ব নির্ণয় করে। পৃথিবীকে R = ৬৩৭১ কিমি ব্যাসার্ধের নিখুঁত গোলক ধরা হয় — বেশিরভাগ দূরত্বে ~০.৫% নির্ভুল।"}
          </p>
          <pre className="overflow-x-auto rounded-lg border border-border bg-muted/40 p-3 text-xs">
{`a = sin²(Δφ/2) + cos(φ₁) × cos(φ₂) × sin²(Δλ/2)
c = 2 × atan2(√a, √(1 − a))
distance = R × c       (R = 6,371 km)`}
          </pre>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Air vs road vs train" : "আকাশ, সড়ক ও ট্রেনের পার্থক্য"}
          </h2>
          <ul className={cn("ml-5 list-disc space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Air = great-circle (shortest). Dhaka–Chittagong ≈ 244 km." : "আকাশ = great-circle (সংক্ষিপ্ততম)। ঢাকা–চট্টগ্রাম ≈ ২৪৪ কিমি।"}</li>
            <li>{lang === "en" ? "Road = follows highways, bridges, rivers — typically 20–40% longer than air." : "সড়ক = মহাসড়ক, সেতু, নদী ঘুরে যায় — সাধারণত আকাশপথের চেয়ে ২০–৪০% বেশি।"}</li>
            <li>{lang === "en" ? "Train = follows fixed track alignment — often longest of the three." : "ট্রেন = নির্দিষ্ট ট্র্যাক ধরে চলে — অনেক সময় সবচেয়ে দীর্ঘ।"}</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Travel time estimation in BD" : "বাংলাদেশে ভ্রমণ সময়ের আনুমানিক হিসাব"}
          </h2>
          <ul className={cn("ml-5 list-disc space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Highway average: 50–60 km/h (Dhaka–Chittagong, Dhaka–Sylhet)." : "মহাসড়কে গড় গতি: ৫০–৬০ কিমি/ঘণ্টা।"}</li>
            <li>{lang === "en" ? "City roads: 15–25 km/h during peak hours." : "শহরের রাস্তায়: পিক আওয়ারে ১৫–২৫ কিমি/ঘণ্টা।"}</li>
            <li>{lang === "en" ? "Padma Bridge cut Dhaka–Khulna travel time roughly in half (now ~6 hr)." : "পদ্মা সেতুর কারণে ঢাকা–খুলনা সময় প্রায় অর্ধেক (~৬ ঘণ্টা)।"}</li>
            <li>{lang === "en" ? "Always add 30–60 min buffer for ferry, toll, or city congestion." : "ফেরি, টোল বা যানজটের জন্য ৩০–৬০ মিনিট অতিরিক্ত ধরুন।"}</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"}
          </h2>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/time-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Time" : "সময়"}</Link>
            <Link to="/feet-to-meter" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Feet ↔ Meter" : "ফুট ↔ মিটার"}</Link>
            <Link to="/cm-to-feet" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">CM ↔ Feet</Link>
            <Link to="/land-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Land" : "জমি"}</Link>
          </div>
        </section>
      </article>
    </CalculatorLayout>
  );
}

const POPULAR_ROUTES: { from: string; to: string }[] = [
  { from: "dhaka", to: "chittagong" },
  { from: "dhaka", to: "sylhet" },
  { from: "dhaka", to: "coxsbazar" },
  { from: "dhaka", to: "rajshahi" },
  { from: "dhaka", to: "khulna" },
  { from: "dhaka", to: "rangpur" },
  { from: "dhaka", to: "barisal" },
  { from: "dhaka", to: "mymensingh" },
  { from: "dhaka", to: "comilla" },
  { from: "dhaka", to: "jessore" },
  { from: "chittagong", to: "coxsbazar" },
];

/* -------------------- Tab 1: Cities -------------------- */

type Mode = "road" | "air" | "train";

function CitiesTab({ lang, useBn, num, fmt }: { lang: Lang; useBn: boolean; num: (n: number | string) => string; fmt: (n: number, d?: number) => string }) {
  const [from, setFrom] = useState("dhaka");
  const [to, setTo] = useState("chittagong");
  const [mode, setMode] = useState<Mode>("road");

  const cd = cityDistance(from, to);
  const swap = () => { const f = from; setFrom(to); setTo(f); };

  const grouped = useMemo(() => {
    const g: Record<string, typeof BD_CITIES> = {};
    BD_CITIES.forEach((c) => { (g[c.division] ??= []).push(c); });
    return g;
  }, []);

  const cityOptions = (
    <>
      {Object.entries(grouped).map(([div, cities]) => (
        <optgroup key={div} label={div}>
          {cities.map((c) => (
            <option key={c.key} value={c.key}>
              {lang === "en" ? c.en : `${c.bn} (${c.en})`}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );

  const presetForMode = cd?.preset?.[mode];

  return (
    <div className="space-y-4">
      <div className="grid items-end gap-3 md:grid-cols-[1fr_auto_1fr]">
        <CitySelect label={lang === "en" ? "From" : "থেকে"} value={from} onChange={setFrom} lang={lang}>{cityOptions}</CitySelect>
        <div className="flex items-center justify-center pb-2">
          <button type="button" onClick={swap} aria-label="Swap" className="rounded-full border border-border bg-card p-2 hover:border-accent">
            <ArrowLeftRight size={16} />
          </button>
        </div>
        <CitySelect label={lang === "en" ? "To" : "তে"} value={to} onChange={setTo} lang={lang}>{cityOptions}</CitySelect>
      </div>

      {/* Mode pills */}
      <div className="inline-flex rounded-full border border-border bg-card p-1">
        {(["road", "air", "train"] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
              mode === m ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
              lang === "bn" && "font-bn",
            )}
          >
            {m === "road" ? (lang === "en" ? "Road" : "সড়ক") : m === "air" ? (lang === "en" ? "Air" : "আকাশ") : (lang === "en" ? "Train" : "ট্রেন")}
          </button>
        ))}
      </div>

      {cd && from !== to && (
        <motion.div
          key={`${from}-${to}-${mode}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-accent/40 bg-accent/5 p-6"
        >
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en" ? cd.from.en : cd.from.bn} → {lang === "en" ? cd.to.en : cd.to.bn}
          </p>
          {presetForMode ? (
            <>
              <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">
                {num(presetForMode.km)} <span className="text-base font-normal text-muted-foreground">{lang === "en" ? "km" : "কিমি"}</span>
              </p>
              <p className={cn("mt-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                {(() => { const t = hoursToHm(presetForMode.minutes / 60); return lang === "en" ? `~${t.h}h ${t.m}m by ${mode}` : `~${num(t.h)} ঘণ্টা ${num(t.m)} মিনিট`; })()}
                {" · "}
                {fmt(convertLength(presetForMode.km, "km", "mile"), 1)} {lang === "en" ? "miles" : "মাইল"}
              </p>
            </>
          ) : (
            <>
              <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">
                {fmt(cd.haversineKm, 1)} <span className="text-base font-normal text-muted-foreground">{lang === "en" ? "km" : "কিমি"}</span>
              </p>
              <p className={cn("mt-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                {lang === "en"
                  ? `Direct (Haversine). No preset for ${mode}; showing great-circle distance.`
                  : `সরাসরি (Haversine)। এই মোডের জন্য প্রিসেট নেই; great-circle দূরত্ব দেখানো হলো।`}
              </p>
            </>
          )}
          <div className="mt-4 grid gap-2 text-xs sm:grid-cols-3">
            <Stat label={lang === "en" ? "Direct (Haversine)" : "সরাসরি (Haversine)"} value={`${fmt(cd.haversineKm, 1)} ${lang === "en" ? "km" : "কিমি"}`} />
            <Stat label={lang === "en" ? "Bearing" : "দিকনির্দেশ"} value={`${fmt(cd.bearing, 0)}° ${compassDirection(cd.bearing).short}`} />
            <a
              href={`https://www.google.com/maps/dir/${cd.from.lat},${cd.from.lon}/${cd.to.lat},${cd.to.lon}`}
              target="_blank" rel="noreferrer"
              className="flex items-center justify-center gap-1.5 rounded-lg border border-border bg-card p-2 text-sm hover:border-accent hover:text-accent"
            >
              <ExternalLink size={12} /> {lang === "en" ? "Google Maps" : "গুগল ম্যাপস"}
            </a>
          </div>
        </motion.div>
      )}

      {from === to && (
        <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
          {lang === "en" ? "Pick two different cities." : "দুটি ভিন্ন শহর নির্বাচন করুন।"}
        </p>
      )}
    </div>
  );
}

function CitySelect({ label, value, onChange, lang, children }: { label: string; value: string; onChange: (v: string) => void; lang: Lang; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-muted-foreground">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn("w-full rounded-xl border border-border bg-card px-3 py-3 text-base font-medium focus:border-accent focus:outline-none", lang === "bn" && "font-bn")}
      >
        {children}
      </select>
    </div>
  );
}

/* -------------------- Tab 2: Coords -------------------- */

function CoordsTab({ lang, useBn, num, fmt }: { lang: Lang; useBn: boolean; num: (n: number | string) => string; fmt: (n: number, d?: number) => string }) {
  const [lat1, setLat1] = useState("23.8103");
  const [lon1, setLon1] = useState("90.4125");
  const [lat2, setLat2] = useState("22.3569");
  const [lon2, setLon2] = useState("91.7832");

  const a = { lat: parseFloat(lat1), lon: parseFloat(lon1) };
  const b = { lat: parseFloat(lat2), lon: parseFloat(lon2) };
  const km = haversineKm(a.lat, a.lon, b.lat, b.lon);
  const brg = bearingDeg(a.lat, a.lon, b.lat, b.lon);
  const dir = compassDirection(brg);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-2 rounded-xl border border-border bg-card p-3">
          <p className={cn("text-xs font-semibold uppercase text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Point 1" : "বিন্দু ১"}
          </p>
          <CoordField label={lang === "en" ? "Latitude" : "অক্ষাংশ"} value={lat1} onChange={setLat1} useBn={useBn} />
          <CoordField label={lang === "en" ? "Longitude" : "দ্রাঘিমাংশ"} value={lon1} onChange={setLon1} useBn={useBn} />
        </div>
        <div className="space-y-2 rounded-xl border border-border bg-card p-3">
          <p className={cn("text-xs font-semibold uppercase text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Point 2" : "বিন্দু ২"}
          </p>
          <CoordField label={lang === "en" ? "Latitude" : "অক্ষাংশ"} value={lat2} onChange={setLat2} useBn={useBn} />
          <CoordField label={lang === "en" ? "Longitude" : "দ্রাঘিমাংশ"} value={lon2} onChange={setLon2} useBn={useBn} />
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-accent/40 bg-accent/5 p-6">
        <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
          {lang === "en" ? "Great-circle distance" : "Great-circle দূরত্ব"}
        </p>
        <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">
          {fmt(km, 2)} <span className="text-base font-normal text-muted-foreground">{lang === "en" ? "km" : "কিমি"}</span>
        </p>
        <p className={cn("mt-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
          {fmt(convertLength(km, "km", "mile"), 2)} {lang === "en" ? "miles" : "মাইল"} · {fmt(convertLength(km, "km", "meter"), 0)} {lang === "en" ? "m" : "মিটার"}
        </p>
        <div className="mt-4 grid gap-2 text-xs sm:grid-cols-2">
          <Stat label={lang === "en" ? "Bearing" : "দিকনির্দেশ"} value={`${fmt(brg, 1)}° (${lang === "en" ? dir.en : dir.bn})`} />
          <Stat label={lang === "en" ? "Earth radius assumed" : "ব্যবহৃত পৃথিবীর ব্যাসার্ধ"} value={`${num(6371)} km`} />
        </div>
      </motion.div>
    </div>
  );
}

function CoordField({ label, value, onChange, useBn }: { label: string; value: string; onChange: (v: string) => void; useBn: boolean }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-muted-foreground">{label}</label>
      <input
        type="text"
        inputMode="decimal"
        value={useBn ? toBengaliNumerals(value) : value}
        onChange={(e) => onChange(stripBn(e.target.value))}
        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium tabular-nums focus:border-accent focus:outline-none"
      />
    </div>
  );
}

/* -------------------- Tab 3: Unit converter -------------------- */

function UnitsTab({ lang, useBn, fmt }: { lang: Lang; useBn: boolean; fmt: (n: number, d?: number) => string }) {
  const [amountStr, setAmountStr] = useState("100");
  const [from, setFrom] = useState<LengthUnitKey>("km");
  const amount = parseFloat(amountStr) || 0;
  const all = convertToAll(amount, from);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-muted-foreground">{lang === "en" ? "Amount" : "পরিমাণ"}</label>
          <input
            type="text"
            inputMode="decimal"
            value={useBn ? toBengaliNumerals(amountStr) : amountStr}
            onChange={(e) => setAmountStr(stripBn(e.target.value))}
            className="w-full rounded-xl border border-border bg-card px-4 py-3 text-2xl font-semibold tabular-nums focus:border-accent focus:outline-none"
          />
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-muted-foreground">{lang === "en" ? "From unit" : "একক"}</label>
          <select
            value={from}
            onChange={(e) => setFrom(e.target.value as LengthUnitKey)}
            className={cn("w-full rounded-xl border border-border bg-card px-3 py-3 text-base font-medium focus:border-accent focus:outline-none", lang === "bn" && "font-bn")}
          >
            {LENGTH_UNITS.map((u) => (
              <option key={u.key} value={u.key}>{lang === "en" ? `${u.en} (${u.shortEn})` : `${u.bn} (${u.shortBn})`}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="p-2 text-left">{lang === "en" ? "Unit" : "একক"}</th>
              <th className="p-2 text-right">{lang === "en" ? "Value" : "মান"}</th>
            </tr>
          </thead>
          <tbody>
            {all.map(({ unit, value }) => (
              <tr key={unit.key} className="border-t border-border">
                <td className="p-2">
                  <span className="font-medium">{lang === "en" ? unit.en : unit.bn}</span>{" "}
                  <span className="text-xs text-muted-foreground">({lang === "en" ? unit.shortEn : unit.shortBn})</span>
                </td>
                <td className="p-2 text-right tabular-nums">{fmt(value, value < 1 ? 6 : 4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
        {lang === "en" ? "For dedicated length conversions, see " : "পূর্ণাঙ্গ দৈর্ঘ্য রূপান্তরের জন্য দেখুন: "}
        <Link to="/feet-to-meter" className="underline hover:text-accent">Feet ↔ Meter</Link>.
      </p>
    </div>
  );
}

/* -------------------- Tab 4: Speed × Time -------------------- */

type STMode = "distance" | "time" | "speed";

function SpeedTimeTab({ lang, useBn, num, fmt }: { lang: Lang; useBn: boolean; num: (n: number | string) => string; fmt: (n: number, d?: number) => string }) {
  const [mode, setMode] = useState<STMode>("distance");
  const [speed, setSpeed] = useState("60");
  const [speedUnit, setSpeedUnit] = useState<SpeedUnit>("kmh");
  const [hStr, setHStr] = useState("2");
  const [mStr, setMStr] = useState("0");
  const [distStr, setDistStr] = useState("100");

  const sp = parseFloat(speed) || 0;
  const hours = (parseFloat(hStr) || 0) + (parseFloat(mStr) || 0) / 60;
  const dist = parseFloat(distStr) || 0;

  let resultLabel = "";
  let resultPrimary = "";
  let resultSub = "";

  if (mode === "distance") {
    const km = distanceFromSpeedTime(sp, speedUnit, hours);
    resultLabel = lang === "en" ? "Distance" : "দূরত্ব";
    resultPrimary = `${fmt(km, 2)} ${lang === "en" ? "km" : "কিমি"}`;
    resultSub = `${fmt(convertLength(km, "km", "mile"), 2)} ${lang === "en" ? "miles" : "মাইল"}`;
  } else if (mode === "time") {
    const hrs = timeFromDistanceSpeed(dist, sp, speedUnit);
    const t = hoursToHm(hrs);
    resultLabel = lang === "en" ? "Time required" : "প্রয়োজনীয় সময়";
    resultPrimary = `${num(t.h)}h ${num(t.m)}m`;
    resultSub = `${fmt(hrs, 3)} ${lang === "en" ? "hours" : "ঘণ্টা"}`;
  } else {
    const sNew = speedFromDistanceTime(dist, hours, speedUnit);
    resultLabel = lang === "en" ? "Required speed" : "প্রয়োজনীয় গতি";
    resultPrimary = `${fmt(sNew, 2)} ${speedUnit === "kmh" ? "km/h" : speedUnit === "mph" ? "mph" : "m/s"}`;
    resultSub = "";
  }

  return (
    <div className="space-y-4">
      {/* Mode pills */}
      <div className="inline-flex rounded-full border border-border bg-card p-1">
        {([
          ["distance", lang === "en" ? "Speed × Time → Distance" : "গতি × সময় → দূরত্ব"],
          ["time",     lang === "en" ? "Distance ÷ Speed → Time" : "দূরত্ব ÷ গতি → সময়"],
          ["speed",    lang === "en" ? "Distance ÷ Time → Speed" : "দূরত্ব ÷ সময় → গতি"],
        ] as [STMode, string][]).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setMode(k)}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
              mode === k ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
              lang === "bn" && "font-bn",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {(mode === "distance" || mode === "time") && (
          <div className="rounded-xl border border-border bg-card p-3">
            <p className={cn("text-xs font-semibold uppercase text-muted-foreground", lang === "bn" && "font-bn")}>{lang === "en" ? "Speed" : "গতি"}</p>
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                inputMode="decimal"
                value={useBn ? toBengaliNumerals(speed) : speed}
                onChange={(e) => setSpeed(stripBn(e.target.value))}
                className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-base font-medium tabular-nums focus:border-accent focus:outline-none"
              />
              <select
                value={speedUnit}
                onChange={(e) => setSpeedUnit(e.target.value as SpeedUnit)}
                className="rounded-lg border border-border bg-background px-2 py-2 text-sm focus:border-accent focus:outline-none"
              >
                <option value="kmh">km/h</option>
                <option value="mph">mph</option>
                <option value="ms">m/s</option>
              </select>
            </div>
          </div>
        )}

        {(mode === "distance" || mode === "speed") && (
          <div className="rounded-xl border border-border bg-card p-3">
            <p className={cn("text-xs font-semibold uppercase text-muted-foreground", lang === "bn" && "font-bn")}>{lang === "en" ? "Time" : "সময়"}</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] text-muted-foreground">{lang === "en" ? "Hours" : "ঘণ্টা"}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={useBn ? toBengaliNumerals(hStr) : hStr}
                  onChange={(e) => setHStr(stripBn(e.target.value))}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium tabular-nums focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-[11px] text-muted-foreground">{lang === "en" ? "Minutes" : "মিনিট"}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={useBn ? toBengaliNumerals(mStr) : mStr}
                  onChange={(e) => setMStr(stripBn(e.target.value))}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium tabular-nums focus:border-accent focus:outline-none"
                />
              </div>
            </div>
          </div>
        )}

        {(mode === "time" || mode === "speed") && (
          <div className="rounded-xl border border-border bg-card p-3 md:col-span-2">
            <p className={cn("text-xs font-semibold uppercase text-muted-foreground", lang === "bn" && "font-bn")}>{lang === "en" ? "Distance (km)" : "দূরত্ব (কিমি)"}</p>
            <input
              type="text"
              inputMode="decimal"
              value={useBn ? toBengaliNumerals(distStr) : distStr}
              onChange={(e) => setDistStr(stripBn(e.target.value))}
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-base font-medium tabular-nums focus:border-accent focus:outline-none"
            />
          </div>
        )}
      </div>

      <motion.div key={`${mode}-${speed}-${hStr}-${mStr}-${distStr}-${speedUnit}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-accent/40 bg-accent/5 p-6 text-center">
        <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>{resultLabel}</p>
        <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">{resultPrimary}</p>
        {resultSub && <p className="mt-1 text-sm text-muted-foreground">{resultSub}</p>}
      </motion.div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-2">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums">{value}</p>
    </div>
  );
}
